import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'
import FieldHelp from '../../components/FieldHelp'

interface Payment {
  id: string
  paymentNumber: string
  paymentDate: string
  postingDate: string
  amount: string
  currency: string
  paymentMethod: string
  reference?: string
  description?: string
  invoice?: {
    id: string
    invoiceNumber: string
    clientOrg?: {
      name: string
    }
  }
  expense?: {
    id: string
    expenseNumber: string
    vendor?: string
  }
  account?: {
    code: string
    name: string
  }
  paidToAccount?: {
    code: string
    name: string
  }
  status: string
  postedAt?: string
  postedBy?: string
}

interface Invoice {
  id: string
  invoiceNumber: string
  total: string
  outstandingAmount: string
  clientOrg: {
    name: string
  }
}

interface Expense {
  id: string
  expenseNumber: string
  total: string
  outstandingAmount: string
  vendor?: string
}

interface Account {
  id: string
  code: string
  name: string
  rootType: string
  isBankAccount: boolean
}

const Payments: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission } = usePermissions(user)
  const [payments, setPayments] = useState<Payment[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showAllocationModal, setShowAllocationModal] = useState(false)
  const [showGatewayModal, setShowGatewayModal] = useState(false)
  const [showGatewayConfigModal, setShowGatewayConfigModal] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [paymentGateways, setPaymentGateways] = useState<any[]>([])
  const [providers, setProviders] = useState<any[]>([])
  const [isProcessingGateway, setIsProcessingGateway] = useState(false)
  const [gatewayFormData, setGatewayFormData] = useState({
    name: '',
    provider: '',
    apiKey: '',
    apiSecret: '',
    webhookSecret: '',
    isTestMode: true,
    supportedCurrencies: ['GBP'],
  })
  const [gatewayProcessData, setGatewayProcessData] = useState({
    gatewayId: '',
    customerEmail: '',
    customerName: '',
    paymentMethod: 'card',
  })
  const [allocations, setAllocations] = useState<any[]>([])
  const [unallocatedInvoices, setUnallocatedInvoices] = useState<any[]>([])
  const [unallocatedPurchaseInvoices, setUnallocatedPurchaseInvoices] = useState<any[]>([])
  const [newAllocation, setNewAllocation] = useState({
    invoiceId: '',
    purchaseInvoiceId: '',
    expenseId: '',
    allocatedAmount: '',
  })
  const [isLoadingPayment, setIsLoadingPayment] = useState(false)
  const [paymentType, setPaymentType] = useState<'receive' | 'send' | 'transfer'>('receive')
  const [accounts, setAccounts] = useState<Account[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [formData, setFormData] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    postingDate: new Date().toISOString().split('T')[0],
    amount: '',
    paymentMethod: '',
    reference: '',
    description: '',
    accountId: '',
    paidToAccountId: '',
    invoiceId: '',
    expenseId: '',
    currency: 'GBP',
  })

  useEffect(() => {
    fetchPayments()
    if (showCreateModal) {
      fetchAccounts()
      if (paymentType === 'receive') {
        fetchInvoices()
      } else if (paymentType === 'send') {
        fetchExpenses()
      }
    }
  }, [showCreateModal, paymentType])

  const fetchPayments = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/payments')
      setPayments(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching payments:', error)
      toast.error('Failed to load payments')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true', rootType: 'ASSET' } })
      setAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const fetchInvoices = async () => {
    try {
      const response = await axios.get('/api/erp/invoices', { params: { status: 'POSTED' } })
      setInvoices(response.data.data || [])
    } catch (error) {
      console.error('Error fetching invoices:', error)
    }
  }

  const fetchExpenses = async () => {
    try {
      const response = await axios.get('/api/erp/expenses', { params: { status: 'POSTED' } })
      setExpenses(response.data.data || [])
    } catch (error) {
      console.error('Error fetching expenses:', error)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.amount || !formData.accountId || !formData.paymentMethod) {
      toast.error('Please fill in all required fields')
      return
    }

    if (paymentType === 'receive' && !formData.invoiceId) {
      toast.error('Please select an invoice for payment received')
      return
    }

    if (paymentType === 'send' && !formData.expenseId) {
      toast.error('Please select an expense for payment sent')
      return
    }

    try {
      const payload: any = {
        paymentDate: formData.paymentDate,
        postingDate: formData.postingDate,
        amount: parseFloat(formData.amount),
        paymentMethod: formData.paymentMethod,
        accountId: formData.accountId,
        currency: formData.currency,
        reference: formData.reference || undefined,
        description: formData.description || undefined,
      }

      if (paymentType === 'receive') {
        payload.invoiceId = formData.invoiceId
        payload.paidToAccountId = formData.paidToAccountId || undefined
        payload.partyType = 'CUSTOMER'
      } else if (paymentType === 'send') {
        payload.expenseId = formData.expenseId
        payload.paidToAccountId = formData.paidToAccountId || undefined
        payload.partyType = 'SUPPLIER'
      } else if (paymentType === 'transfer') {
        payload.paidToAccountId = formData.paidToAccountId
        payload.partyType = 'INTERNAL'
      }

      await axios.post('/api/erp/payments', payload)
      toast.success('Payment recorded successfully')
      setShowCreateModal(false)
      setFormData({
        paymentDate: new Date().toISOString().split('T')[0],
        postingDate: new Date().toISOString().split('T')[0],
        amount: '',
        paymentMethod: '',
        reference: '',
        description: '',
        accountId: '',
        paidToAccountId: '',
        invoiceId: '',
        expenseId: '',
        currency: 'GBP',
      })
      fetchPayments()
    } catch (error: any) {
      console.error('Error creating payment:', error)
      toast.error(error.response?.data?.error || 'Failed to record payment')
    }
  }

  const handleViewPayment = async (paymentId: string) => {
    setIsLoadingPayment(true)
    try {
      const response = await axios.get(`/api/erp/payments/${paymentId}`)
      setSelectedPayment(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching payment:', error)
      toast.error('Failed to load payment details')
    } finally {
      setIsLoadingPayment(false)
    }
  }

  const handlePost = async (paymentId: string) => {
    if (!confirm('Are you sure you want to post this payment? This will create GL entries and cannot be undone.')) {
      return
    }
    try {
      await axios.post(`/api/erp/payments/${paymentId}/post`)
      toast.success('Payment posted successfully')
      fetchPayments()
    } catch (error: any) {
      console.error('Error posting payment:', error)
      toast.error(error.response?.data?.error || 'Failed to post payment')
    }
  }

  const handleDelete = async (paymentId: string) => {
    if (!confirm('Are you sure you want to delete this payment? This action cannot be undone.')) {
      return
    }
    try {
      await axios.delete(`/api/erp/payments/${paymentId}`)
      toast.success('Payment deleted successfully')
      fetchPayments()
    } catch (error: any) {
      console.error('Error deleting payment:', error)
      toast.error(error.response?.data?.error || 'Failed to delete payment')
    }
  }

  const handleProcessGateway = async () => {
    if (!selectedPayment) return
    setIsProcessingGateway(true)
    try {
      const response = await axios.post(`/api/erp/payments/${selectedPayment.id}/process-gateway`, gatewayProcessData)
      if (response.data.success) {
        toast.success(`Payment processed successfully. Status: ${response.data.data.gatewayStatus}`)
        setShowGatewayModal(false)
        fetchPayments()
        if (selectedPayment.id) {
          handleViewPayment(selectedPayment.id)
        }
      } else {
        toast.error(response.data.data.error || 'Payment processing failed')
      }
    } catch (error: any) {
      console.error('Error processing payment through gateway:', error)
      toast.error(error.response?.data?.error || 'Failed to process payment through gateway')
    } finally {
      setIsProcessingGateway(false)
    }
  }

  const handleCreateGateway = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await axios.post('/api/erp/payment-gateways', gatewayFormData)
      toast.success('Payment gateway created successfully')
      setShowGatewayConfigModal(false)
      setGatewayFormData({
        name: '',
        provider: '',
        apiKey: '',
        apiSecret: '',
        webhookSecret: '',
        isTestMode: true,
        supportedCurrencies: ['GBP'],
      })
      fetchPaymentGateways()
    } catch (error: any) {
      console.error('Error creating payment gateway:', error)
      toast.error(error.response?.data?.error || 'Failed to create payment gateway')
    }
  }

  if (!hasPermission('erp_payment', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view payments.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
            <PageHelp pageIdentifier="payments" title="Payments" />
          </div>
          <p className="text-gray-600 mt-1">Payment processing and tracking</p>
        </div>
        {hasPermission('erp_payment', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Record Payment</span>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : payments.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No payments found. Record your first payment to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Linked To</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewPayment(payment.id)}>
                  <td className="px-4 py-2">{payment.paymentNumber}</td>
                  <td className="px-4 py-2">{new Date(payment.paymentDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2">
                    {payment.invoice ? `Invoice ${payment.invoice.invoiceNumber}` : payment.expense ? `Expense ${payment.expense.expenseNumber}` : '-'}
                  </td>
                  <td className="px-4 py-2 text-right">{parseFloat(payment.amount).toLocaleString('en-GB', { style: 'currency', currency: payment.currency || 'GBP' })}</td>
                  <td className="px-4 py-2">{payment.paymentMethod}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      payment.status === 'POSTED' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewPayment(payment.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        disabled={isLoadingPayment}
                      >
                        View
                      </button>
                      {payment.status === 'DRAFT' && hasPermission('erp_payment', 'write') && (
                        <button
                          onClick={() => handleAllocate(payment)}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Allocate
                        </button>
                      )}
                      {payment.status === 'DRAFT' && hasPermission('erp_payment', 'post') && (
                        <button
                          onClick={() => handlePost(payment.id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Post
                        </button>
                      )}
                      {payment.status === 'DRAFT' && hasPermission('erp_payment', 'delete') && (
                        <button
                          onClick={() => handleDelete(payment.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Payment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Record Payment</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                {/* Payment Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type *</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentType('receive')
                        setFormData({ ...formData, invoiceId: '', expenseId: '' })
                      }}
                      className={`px-4 py-2 rounded border text-sm ${
                        paymentType === 'receive' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      Receive Money
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentType('send')
                        setFormData({ ...formData, invoiceId: '', expenseId: '' })
                      }}
                      className={`px-4 py-2 rounded border text-sm ${
                        paymentType === 'send' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      Send Money
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentType('transfer')
                        setFormData({ ...formData, invoiceId: '', expenseId: '' })
                      }}
                      className={`px-4 py-2 rounded border text-sm ${
                        paymentType === 'transfer' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      Internal Transfer
                    </button>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Date *
                      <FieldHelp content="The date when the payment was made or received. This is the actual transaction date when money changed hands." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.paymentDate}
                      onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Posting Date *
                      <FieldHelp content="The date used for accounting purposes. This determines which accounting period the payment belongs to and appears in financial reports. Usually the same as payment date, but can differ for period adjustments or bank reconciliation timing." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.postingDate}
                      onChange={(e) => setFormData({ ...formData, postingDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                {/* Amount and Currency */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="GBP">GBP</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                  <select
                    required
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select payment method...</option>
                    <option value="CASH">Cash</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CHEQUE">Cheque</option>
                    <option value="CREDIT_CARD">Credit Card</option>
                    <option value="DEBIT_CARD">Debit Card</option>
                    <option value="STRIPE">Stripe</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                {/* Bank Account */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank/Cash Account *</label>
                  <select
                    required
                    value={formData.accountId}
                    onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select account...</option>
                    {accounts.filter(acc => acc.isBankAccount || acc.rootType === 'ASSET').map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                    ))}
                  </select>
                </div>

                {/* Invoice/Expense Selection */}
                {paymentType === 'receive' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice *</label>
                    <select
                      required
                      value={formData.invoiceId}
                      onChange={(e) => {
                        const invoice = invoices.find(inv => inv.id === e.target.value)
                        setFormData({
                          ...formData,
                          invoiceId: e.target.value,
                          amount: invoice ? parseFloat(invoice.outstandingAmount).toString() : formData.amount
                        })
                      }}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="">Select invoice...</option>
                      {invoices.map(inv => (
                        <option key={inv.id} value={inv.id}>
                          {inv.invoiceNumber} - {inv.clientOrg.name} ({parseFloat(inv.outstandingAmount).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {paymentType === 'send' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expense *</label>
                    <select
                      required
                      value={formData.expenseId}
                      onChange={(e) => {
                        const expense = expenses.find(exp => exp.id === e.target.value)
                        setFormData({
                          ...formData,
                          expenseId: e.target.value,
                          amount: expense ? parseFloat(expense.outstandingAmount).toString() : formData.amount
                        })
                      }}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="">Select expense...</option>
                      {expenses.map(exp => (
                        <option key={exp.id} value={exp.id}>
                          {exp.expenseNumber} - {exp.vendor || 'N/A'} ({parseFloat(exp.outstandingAmount).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {paymentType === 'transfer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transfer To Account *</label>
                    <select
                      required
                      value={formData.paidToAccountId}
                      onChange={(e) => setFormData({ ...formData, paidToAccountId: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="">Select account...</option>
                      {accounts.filter(acc => acc.isBankAccount || acc.rootType === 'ASSET').map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Reference and Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                  <input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    placeholder="Check number, transaction ID, etc."
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    placeholder="Additional notes..."
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Payment Modal */}
      {showViewModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Payment {selectedPayment.paymentNumber}</h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedPayment(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Payment Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedPayment.paymentDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Posting Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedPayment.postingDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedPayment.status === 'POSTED' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedPayment.status}
                    </span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount</label>
                  <p className="mt-1 text-lg font-bold text-gray-900">
                    {parseFloat(selectedPayment.amount).toLocaleString('en-GB', { style: 'currency', currency: selectedPayment.currency || 'GBP' })}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedPayment.paymentMethod}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Bank/Cash Account</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedPayment.account ? `${selectedPayment.account.code} - ${selectedPayment.account.name}` : '-'}
                  </p>
                </div>
                {selectedPayment.paidToAccount && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Transfer To Account</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {selectedPayment.paidToAccount.code} - {selectedPayment.paidToAccount.name}
                    </p>
                  </div>
                )}
              </div>

              {selectedPayment.invoice && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Linked Invoice</label>
                  <p className="mt-1 text-sm text-gray-900">
                    Invoice {selectedPayment.invoice.invoiceNumber}
                    {selectedPayment.invoice.clientOrg && ` - ${selectedPayment.invoice.clientOrg.name}`}
                  </p>
                </div>
              )}

              {selectedPayment.expense && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Linked Expense</label>
                  <p className="mt-1 text-sm text-gray-900">
                    Expense {selectedPayment.expense.expenseNumber}
                    {selectedPayment.expense.vendor && ` - ${selectedPayment.expense.vendor}`}
                  </p>
                </div>
              )}

              {selectedPayment.reference && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reference</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedPayment.reference}</p>
                </div>
              )}

              {selectedPayment.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedPayment.description}</p>
                </div>
              )}

              {/* Posted Info */}
              {selectedPayment.postedAt && (
                <div className="text-sm text-gray-500">
                  Posted on {new Date(selectedPayment.postedAt).toLocaleString()}
                  {selectedPayment.postedBy && ` by ${selectedPayment.postedBy}`}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-between">
              <div>
                {selectedPayment.status === 'DRAFT' && hasPermission('erp_payment', 'write') && paymentGateways.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setGatewayProcessData({
                        gatewayId: paymentGateways[0].id,
                        customerEmail: '',
                        customerName: '',
                        paymentMethod: 'card',
                      })
                      setShowGatewayModal(true)
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    Process via Gateway
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedPayment(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Allocation Modal */}
      {showAllocationModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Allocate Payment: {selectedPayment.paymentNumber}</h2>
            
            <div className="mb-4 p-4 bg-gray-50 rounded">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Amount</label>
                  <p className="text-lg font-semibold">
                    {parseFloat(selectedPayment.amount).toLocaleString('en-GB', { style: 'currency', currency: selectedPayment.currency || 'GBP' })}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Total Allocated</label>
                  <p className={`text-lg font-semibold ${
                    allocations.reduce((sum, a) => sum + parseFloat(a.allocatedAmount), 0) > parseFloat(selectedPayment.amount)
                      ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {allocations.reduce((sum, a) => sum + parseFloat(a.allocatedAmount), 0).toLocaleString('en-GB', { style: 'currency', currency: selectedPayment.currency || 'GBP' })}
                  </p>
                </div>
              </div>
            </div>

            {/* Existing Allocations */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Existing Allocations</h3>
              {allocations.length === 0 ? (
                <p className="text-gray-500 text-sm">No allocations yet</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {allocations.map((alloc) => (
                      <tr key={alloc.id}>
                        <td className="px-4 py-2 text-sm">
                          {alloc.invoice ? 'Invoice' : alloc.purchaseInvoice ? 'Purchase Invoice' : 'Expense'}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {alloc.invoice && `Invoice ${alloc.invoice.invoiceNumber} - ${alloc.invoice.clientOrg?.name || ''}`}
                          {alloc.purchaseInvoice && `Purchase Invoice ${alloc.purchaseInvoice.invoiceNumber} - ${alloc.purchaseInvoice.supplier?.name || ''}`}
                          {alloc.expense && `Expense ${alloc.expense.expenseNumber} - ${alloc.expense.vendor || ''}`}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          {parseFloat(alloc.allocatedAmount).toLocaleString('en-GB', { style: 'currency', currency: alloc.currency || 'GBP' })}
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleDeleteAllocation(alloc.id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Add New Allocation */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-3">Add Allocation</h3>
              <div className="space-y-4">
                {unallocatedInvoices.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice</label>
                    <select
                      value={newAllocation.invoiceId}
                      onChange={(e) => {
                        const invoice = unallocatedInvoices.find(inv => inv.id === e.target.value)
                        setNewAllocation({
                          ...newAllocation,
                          invoiceId: e.target.value,
                          purchaseInvoiceId: '',
                          expenseId: '',
                          allocatedAmount: invoice ? parseFloat(invoice.outstandingAmount).toString() : '',
                        })
                      }}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    >
                      <option value="">Select invoice...</option>
                      {unallocatedInvoices.map(inv => (
                        <option key={inv.id} value={inv.id}>
                          {inv.invoiceNumber} - {inv.clientOrg.name} (Outstanding: {parseFloat(inv.outstandingAmount).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allocated Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newAllocation.allocatedAmount}
                    onChange={(e) => setNewAllocation({ ...newAllocation, allocatedAmount: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    placeholder="0.00"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAllocationModal(false)
                      setSelectedPayment(null)
                      setAllocations([])
                      setNewAllocation({ invoiceId: '', purchaseInvoiceId: '', expenseId: '', allocatedAmount: '' })
                    }}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleAddAllocation}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Add Allocation
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Payments
